class GeolocationMarkets extends HTMLElement {
  constructor() {
    super();
    this.countries = null;
    this.continents = null;
    this.currentLocation = null;
    this.activeContinent = null;
    this.activeCountry = null;
    this.activeCountryIsoCode = null;
    this.activeLanguage = null;

    this._selectContinent = null;
    this._selectCountry = null;
    this._selectLanguage = null;

    this._updateButton = null;
    
    // Store initial value to compare against
    this._initialCountry = null;
  }

  connectedCallback() {
    this.countries = this.createCountriesObject(this.dataset.countries);
    this.continents = this.dataset.continents;

    // Parse JSON-encoded strings to handle special characters properly
    this.currentLocation = JSON.parse(this.dataset.currentLocation || '""');
    this.activeContinent = JSON.parse(this.dataset.activeContinent || '""');
    this.activeCountry = this.currentLocation;

    this._selectContinent = this.querySelector("[data-select='continent']");
    this._selectCountry = this.querySelector("[data-select='country']");
    this._selectLanguage = this.querySelector("[data-select='language']");

    this._updateButton = this.querySelector("[data-update-location]");

    // Get initial ISO code from the currently selected country
    this._setInitialCountryIsoCode();

    // Update initial display with currency
    this._updateInitialLocationDisplay();

    // Set initial language
    this.activeLanguage = this._getCurrentLanguageCode();

    // Store initial value
    this._initialCountry = this.activeCountry;

    this.init();

    this._selectContinent.addEventListener("change", this.selectTrigger.bind(this));
    this._selectCountry.addEventListener("change", this.selectTrigger.bind(this));
    this._selectLanguage?.addEventListener("change", this.selectTrigger.bind(this));
    
    // Wait for Weglot to load and set up language observer
    this._setupWeglotObserver();
    
    // Preselect continent based on current location
    this._preselectContinentFromCurrentLocation();
    
    // Initially hide the button
    this._toggleUpdateButton();

    // Populate and preselect languages from Weglot
    this._populateLanguagesFromWeglot();
  }

  init() {
    this._updateButton.addEventListener("click", this.updateLocation.bind(this));
  }

  async _preselectContinentFromCurrentLocation() {
    // Step 1: Populate continents dropdown
    await this._populateContinents();
    
    // Step 2: Find and preselect the active continent
    if (!this.currentLocation || !this.countries) return;
    
    const allCountries = JSON.parse(this.countries || '[]');
    const currentCountryData = allCountries.find(country => country.country_name === this.currentLocation);
    
    if (!currentCountryData) return;
    
    const continentName = currentCountryData.continent_name;
    
    // Set the active continent
    this.activeContinent = continentName;
    
    // Find the continent option with this value
    const continentOption = this._selectContinent.querySelector(`option[value="${continentName}"]`);
    
    if (continentOption) {
      // Set as selected
      this._selectContinent.value = continentName;
      
      // Manually trigger country population (change event won't fire if value was already set)
      // or if it does fire, the check in selectTrigger will prevent duplicate call
      await this.changeCountries({
        target: this._selectContinent,
        detail: {
          value: continentName
        }
      });
    }
  }

  async _populateContinents() {
    if (!this.continents || !this._selectContinent) return;
    
    const allContinents = JSON.parse(this.continents || '[]');
    
    // Clear existing options
    this._selectContinent.innerHTML = '';
    
    // Translate label
    const Weglot = window.Weglot;
    let labelText = 'Update Region';
    if (Weglot?.initialized === true && Weglot.getCurrentLang() !== 'en') {
      const labelTranslated = await translateArray([{name: 'Update Region'}], ['name'], Weglot.getCurrentLang());
      labelText = labelTranslated[0].name;
    }
    
    // Recreate placeholder as first option (disabled, visible)
    const placeholder = createOption(labelText, '', null, true, false);
    this._selectContinent.appendChild(placeholder);
    
    // Add all continents
    for (const continent of allContinents) {
      const continentName = continent.continent_name || continent.name;
      const option = createOption(
        continentName,
        continentName,
        null,
        false,
        false
      );
      this._selectContinent.appendChild(option);
    }
    
    // Always set a valid value - either the active continent or the first continent
    if (this.activeContinent) {
      const continentOption = this._selectContinent.querySelector(`option[value="${this.activeContinent}"]`);
      if (continentOption) {
        this._selectContinent.value = this.activeContinent;
      } else if (allContinents.length > 0) {
        // Fallback to first continent if active continent not found
        const firstContinent = allContinents[0];
        const firstContinentName = firstContinent.continent_name || firstContinent.name;
        this.activeContinent = firstContinentName;
        this._selectContinent.value = firstContinentName;
      }
    } else if (allContinents.length > 0) {
      // No active continent, use first one
      const firstContinent = allContinents[0];
      const firstContinentName = firstContinent.continent_name || firstContinent.name;
      this.activeContinent = firstContinentName;
      this._selectContinent.value = firstContinentName;
    }
  }

  createCountriesObject(countriesString) {
    let cleanedString = '';
    
    try {
      // Remove wrapping quotes if present
      cleanedString = countriesString.trim();
      if (cleanedString.startsWith('"') && cleanedString.endsWith('"')) {
        cleanedString = cleanedString.slice(1, -1);
      }
      
      // Handle unicode escapes FIRST - convert \u0026 to the literal character
      // This needs to happen before we unescape quotes
      cleanedString = cleanedString.replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => {
        return String.fromCharCode(parseInt(code, 16));
      });
      
      // Unescape backslashes - \" becomes "
      cleanedString = cleanedString.replace(/\\"/g, '"');
      
      // Remove any remaining stray backslashes that aren't valid escape sequences
      // This handles cases like \& that shouldn't be there
      cleanedString = cleanedString.replace(/\\(?!["\\/bfnrt])/g, '');
      
      // Add opening bracket if missing
      if (!cleanedString.startsWith('[')) {
        cleanedString = '[' + cleanedString;
      }
      
      // Add closing bracket if missing
      if (!cleanedString.endsWith(']')) {
        cleanedString = cleanedString + ']';
      }
      
      // Fix the malformed JSON structure BEFORE decoding HTML entities
      // From: {fc:false,"cn:Asia","iso:AF","con:Afghanistan"}
      // To: {"fc":false,"cn":"Asia","iso":"AF","con":"Afghanistan"}
      
      // Step 1: Fix the fc: key (handle both true and false)
      cleanedString = cleanedString.replace(/\{fc:true/g, '{"fc":true');
      cleanedString = cleanedString.replace(/\{fc:false/g, '{"fc":false');
      
      // Step 2: Fix the string format "cn:Asia" to "cn":"Asia"
      cleanedString = cleanedString.replace(/"cn:([^"]+)"/g, '"cn":"$1"');
      cleanedString = cleanedString.replace(/"iso:([^"]+)"/g, '"iso":"$1"');
      cleanedString = cleanedString.replace(/"con:([^"]+)"/g, '"con":"$1"');
      
      // Now decode HTML entities like &amp; -> & and &#39; -> '
      const textarea = document.createElement('textarea');
      textarea.innerHTML = cleanedString;
      cleanedString = textarea.value;
      
      // Parse the cleaned JSON
      const parsed = JSON.parse(cleanedString);
      
      // Transform to our expected format
      const transformedCountries = parsed.map(country => ({
        continent_name: country.cn || '',
        country_name: country.con || '',
        featured_country: country.fc === true || country.fc === 'true',
        iso_code: country.iso || ''
      }));
      
      return JSON.stringify(transformedCountries);
    } catch (error) {
      console.error("❌ Failed to parse countries:", error);
      
      // Show context around the error from the cleaned string
      if (error.message.includes('position')) {
        const position = parseInt(error.message.match(/position (\d+)/)?.[1] || 0);
        const start = Math.max(0, position - 150);
        const end = Math.min(cleanedString.length, position + 150);
      }
      
      return '[]';
    }
  }

  _setInitialCountryIsoCode() {
    // Find the currently selected country option
    const selectedOption = this._selectCountry.querySelector(`option[value="${this.activeCountry}"]`);
    if (selectedOption && selectedOption.dataset.isoCode) {
      this.activeCountryIsoCode = selectedOption.dataset.isoCode;
    } else {
      // Fallback: try to find it from the countries data
      const allCountries = JSON.parse(this.countries || '[]');
      const currentCountry = allCountries.find(country => country.country_name === this.activeCountry);
      if (currentCountry) {
        this.activeCountryIsoCode = currentCountry.iso_code;
      }
    }
  }

  _getCurrencyForCountry(isoCode) {
    if (!isoCode || !window.availableCountriesCurrecy) return null;
    
    const countryCurrency = window.availableCountriesCurrecy.find(
      country => country.iso_code.toLowerCase() === isoCode.toLowerCase()
    );
    
    if (countryCurrency && countryCurrency.currency) {
      return {
        code: countryCurrency.currency.iso_code,
        symbol: countryCurrency.currency.symbol
      };
    }
    
    return null;
  }

  _formatCountryWithCurrency(countryName, isoCode) {
    const currency = this._getCurrencyForCountry(isoCode);
    
    if (currency) {
      return `${countryName} (${currency.code} ${currency.symbol})`;
    }
    
    return countryName;
  }

  _updateInitialLocationDisplay() {
    if (this.activeCountry && this.activeCountryIsoCode) {
      const currentLocationElement = this.querySelector("[data-current-location]");
      if (currentLocationElement) {
        const formattedText = this._formatCountryWithCurrency(this.activeCountry, this.activeCountryIsoCode);
        currentLocationElement.innerHTML = formattedText;
      }
    }
  }

  _getCurrentLanguageCode() {
    const Weglot = window.Weglot;
    return Weglot?.initialized ? Weglot.getCurrentLang() : 'en';
  }

  _populateLanguagesFromWeglot() {
    const Weglot = window.Weglot;
    
    if (!Weglot?.initialized || !this._selectLanguage) return;

    try {
      // Get available languages from Weglot options or configuration
      let languages = null;

      // Try different ways to access languages from Weglot
      if (Weglot.options && Weglot.options.destination_languages) {
        languages = Weglot.options.destination_languages;
      } else if (Weglot.options && Weglot.options.languages) {
        languages = Weglot.options.languages;
      } else if (Weglot.languages) {
        languages = Weglot.languages;
      } else if (Weglot.getAvailableLanguages) {
        languages = Weglot.getAvailableLanguages();
      } else {

        // Try common property names
        if (Weglot.options && Weglot.options.destinations) {
          languages = Weglot.options.destinations;
        } else {
          console.warn('Could not find languages in Weglot instance');
          return;
        }
      }

      if (!languages || !Array.isArray(languages)) {
        console.warn('Languages data not found or not an array:', languages);
        return;
      }

      // Clear existing options except the placeholder
      const placeholder = this._selectLanguage.querySelector('option[value=""]');
      this._selectLanguage.innerHTML = '';
      if (placeholder) {
        this._selectLanguage.appendChild(placeholder);
      }

      // Manually add English as the first option
      const englishOption = document.createElement('option');
      englishOption.value = 'en';
      englishOption.textContent = 'English';
      englishOption.dataset.languageName = 'English';
      englishOption.dataset.languageCode = 'en';

      // Mark as selected if English is the current language
      if ('en' === Weglot.getCurrentLang()) {
        englishOption.selected = true;
      }

      this._selectLanguage.appendChild(englishOption);

      // Add language options
      languages.forEach(language => {
        // Handle different possible language object structures
        let langCode, langName;

        if (typeof language === 'string') {
          // Simple string format: "en", "fr", etc.
          langCode = language;
          langName = language.toUpperCase();
        } else if (language.language_to) {
          // Weglot format: { language_to: "en", language_to_name: "English" }
          langCode = language.language_to;
          langName = language.language_to_name || language.language_to.toUpperCase();
        } else if (language.code) {
          // Alternative format: { code: "en", name: "English" }
          langCode = language.code;
          langName = language.name || language.code.toUpperCase();
        } else {
          console.warn('Unknown language object structure:', language);
          return;
        }

        const option = document.createElement('option');
        option.value = langCode;
        option.textContent = Weglot.getLanguageName(langCode);
        option.dataset.languageName = langName;
        option.dataset.languageCode = langCode;

        // Mark as selected if it's the current language
        if (langCode === Weglot.getCurrentLang()) {
          option.selected = true;
        }

        this._selectLanguage.appendChild(option);
      });

      // Set active language
      this.activeLanguage = Weglot.getCurrentLang();

      // Update current language display
      const currentLanguageElement = this.querySelector("[data-current-language]");
      if (currentLanguageElement) {
        const currentLangOption = this._selectLanguage.querySelector(`option[value="${this.activeLanguage}"]`);
        if (currentLangOption) {
          currentLanguageElement.textContent = currentLangOption.dataset.languageName || currentLangOption.textContent.trim();
        }
      }

    } catch (error) {
      console.error('Failed to populate languages from Weglot:', error);
    }
  }

  _setupWeglotObserver() {
    // Wait for Weglot to initialize
    const checkWeglot = setInterval(() => {
      const Weglot = window.Weglot;

      if (Weglot?.initialized) {
        clearInterval(checkWeglot);

        // Populate languages and set initial language
        this._populateLanguagesFromWeglot();

        // Set up language change listener
        Weglot.on("languageChanged", (newLang, prevLang) => {
          this._handleLanguageChange(newLang, prevLang);
        });
      }
    }, 100);

    // Stop checking after 10 seconds if Weglot hasn't loaded
    setTimeout(() => {
      clearInterval(checkWeglot);
      console.warn("⚠️ Weglot not initialized after 10 seconds");
    }, 10000);
  }

  _handleLanguageChange(newLang, prevLang) {
    // Update our active language
    this.activeLanguage = newLang;

    // Update the select value
    if (this._selectLanguage) {
      this._selectLanguage.value = newLang;
    }

    // Update the current language display
    const currentLanguageElement = this.querySelector("[data-current-language]");
    if (currentLanguageElement) {
      const currentLangOption = this._selectLanguage?.querySelector(`option[value="${newLang}"]`);
      if (currentLangOption) {
        currentLanguageElement.textContent = currentLangOption.dataset.languageName || currentLangOption.textContent.trim();
      }
    }

    // Update trigger texts with translations
    setTimeout(() => {
      this._updateTriggerTexts();
    }, 100);
  }

  async _updateTriggerTexts() {
    const Weglot = window.Weglot;
    if (!Weglot?.initialized) return;

    const currentLang = Weglot.getCurrentLang();

    // Update continent select value if continent is selected
    if (this.activeContinent && this._selectContinent) {
      const continentOption = this._selectContinent.querySelector(`option[value="${this.activeContinent}"]`);
      if (continentOption) {
        this._selectContinent.value = this.activeContinent;
      }
    }

    // Re-populate countries dropdown with translated names if continent is selected
    if (this.activeContinent && this._selectCountry) {
      await this.changeCountries({
        target: this._selectContinent,
        detail: {
          value: this.activeContinent
        }
      });
    } else if (this.activeCountry && this._selectCountry) {
      // Update country select value
      const countryOption = this._selectCountry.querySelector(`option[value="${this.activeCountry}"]`);
      if (countryOption) {
        this._selectCountry.value = this.activeCountry;
      }
    }

    // Update data-current-location with translated country name and currency
    if (this.activeCountry) {
      const currentLocationElement = this.querySelector("[data-current-location]");
      if (currentLocationElement) {
        // Try to get translated text from the selected option (already includes currency)
        const countryOption = this._selectCountry?.querySelector(`option[value="${this.activeCountry}"]`);
        if (countryOption) {
          // Use the text content directly (already formatted with currency)
          currentLocationElement.innerHTML = countryOption.textContent.trim();
        } else {
          // If option not found, translate and format
          let countryName = this.activeCountry;
          if (currentLang !== 'en') {
            const translatedCountries = await translateArray([{name: this.activeCountry}], ['name'], currentLang);
            countryName = translatedCountries[0].name;
          }
          // Format with currency
          const formattedText = this._formatCountryWithCurrency(countryName, this.activeCountryIsoCode);
          currentLocationElement.innerHTML = formattedText;
        }
      }
    }
  }

  selectTrigger(e) {
    const select = e.target;
    const dataset = select.dataset;
    const value = select.value;

    if(dataset.select === "continent") {
      if(value !== this.activeContinent) {
        this.changeCountries({
          target: select,
          detail: { value: value }
        });
      }

      this.activeContinent = value;
    }

    if(dataset.select === "country") {
      this.activeCountry = value;

      // Get the ISO code from the selected option
      const selectedOption = this._selectCountry.querySelector(`option[value="${value}"]`);
      if (selectedOption && selectedOption.dataset.isoCode) {
        this.activeCountryIsoCode = selectedOption.dataset.isoCode;
      } else {
        console.error('❌ Could not find ISO code for selected country:', value);
      }

      // Update data-current-location with the translated text from the selected option and currency
      const currentLocationElement = this.querySelector("[data-current-location]");
      if (currentLocationElement && selectedOption) {
        // Use the text content from the selected option (which already includes currency)
        const formattedText = selectedOption.textContent.trim();
        currentLocationElement.innerHTML = formattedText;
      } else if (currentLocationElement) {
        // Fallback to the value if option not found
        const formattedText = this._formatCountryWithCurrency(this.activeCountry, this.activeCountryIsoCode);
        currentLocationElement.innerHTML = formattedText;
      }
    }

    if(dataset.select === "language") {
      this.activeLanguage = value;

      // Update data-current-language display
      const currentLanguageElement = this.querySelector("[data-current-language]");
      const selectedOption = this._selectLanguage.querySelector(`option[value="${value}"]`);
      if (currentLanguageElement && selectedOption) {
        currentLanguageElement.textContent = selectedOption.dataset.languageName || selectedOption.textContent.trim();
      }
    }

    // Check if any values changed and toggle button visibility
    this._toggleUpdateButton();
  }

  async changeCountries(e) {
    const selectedContinentName = e.detail.value;
    let allCountries = JSON.parse(this.countries || '[]');

    const Weglot = window.Weglot;

    if(Weglot?.initialized === true && Weglot.getCurrentLang() !== 'en') {
      allCountries = await translateArray(allCountries, ['country_name'], Weglot.getCurrentLang());
    }

    // Filter countries by continent_name
    const continentCountries = allCountries.filter(country => {
      return country.continent_name === selectedContinentName;
    });

    // Separate featured and regular countries
    const featuredCountries = continentCountries.filter(country => country.featured_country === true);
    const regularCountries = continentCountries.filter(country => country.featured_country !== true);
    
    const labelTranslated = await translateArray([{name: 'Update Country'}], ['name'], Weglot.getCurrentLang())
    
    // Clear existing options
    this._selectCountry.innerHTML = '';
    
    // Recreate placeholder as first option (disabled, visible)
    const placeholder = createOption(labelTranslated[0].name, '', null, true, false);
    this._selectCountry.appendChild(placeholder);
    
    // Add featured countries
    for (const country of featuredCountries) {
      const displayText = this._formatCountryWithCurrency(country.country_name, country.iso_code);
      const option = createOption(
        displayText,
        country.country_name,
        {
          countryName: country.country_name,
          isoCode: country.iso_code
        },
        false,
        false // Don't auto-select any
      );
      this._selectCountry.appendChild(option);
    }

    // Add divider between featured and regular countries
    if (featuredCountries.length > 0 && regularCountries.length > 0) {
      const divider = createOption('---', '', null, true, false);
      this._selectCountry.appendChild(divider);
    }
    
    // Add regular countries
    for (const country of regularCountries) {
      const displayText = this._formatCountryWithCurrency(country.country_name, country.iso_code);
      const option = createOption(
        displayText,
        country.country_name,
        {
          countryName: country.country_name,
          isoCode: country.iso_code
        },
        false,
        false // Don't auto-select any
      );
      this._selectCountry.appendChild(option);
    }
    
    // Always set a valid value - either the active country or the first country
    if (this.activeCountry) {
      const currentCountryOption = this._selectCountry.querySelector(`option[value="${this.activeCountry}"]`);
    if (currentCountryOption) {
        this._selectCountry.value = this.activeCountry;
      } else if (featuredCountries.length > 0 || regularCountries.length > 0) {
        // If current country not found, use first country as fallback
        const firstCountry = featuredCountries[0] || regularCountries[0];
        if (firstCountry) {
          this.activeCountry = firstCountry.country_name;
          this.activeCountryIsoCode = firstCountry.iso_code;
          this._selectCountry.value = firstCountry.country_name;
        }
      }
    } else if (featuredCountries.length > 0 || regularCountries.length > 0) {
      // No active country, use first available
      const firstCountry = featuredCountries[0] || regularCountries[0];
      if (firstCountry) {
      this.activeCountry = firstCountry.country_name;
      this.activeCountryIsoCode = firstCountry.iso_code;
        this._selectCountry.value = firstCountry.country_name;
      }
    }
    
    // Check if button should be shown after changing countries
    this._toggleUpdateButton();
  }

  _toggleUpdateButton() {
    const countryChanged = this.activeCountry !== this._initialCountry;
    const languageChanged = this.activeLanguage !== this._getCurrentLanguageCode();

    if (countryChanged || languageChanged) {
      this._updateButton.style.display = 'block';
    } else {
      this._updateButton.style.display = 'none';
    }
  }

  updateLocation() {
    const countryChanged = this.activeCountry !== this._initialCountry;
    const languageChanged = this.activeLanguage !== this._getCurrentLanguageCode();

    // Handle country change
    if (countryChanged) {
      this._updateCountry();
    }

    // Handle language change
    if (languageChanged) {
      this._updateLanguage();
    }
  }

  _updateCountry() {
    if (!this.activeCountryIsoCode) {
      console.error('No ISO code available for selected country');
      return;
    }

    if(this.activeCountryIsoCode.toLowerCase() === 'us') {
      window.location.href = window.location.origin;
      return;
    }

    // Get base URL (protocol + hostname)
    const baseURL = `${window.location.protocol}//${window.location.host}`;

    // Build new URL with country ISO code
    const newURL = `${baseURL}/en-${this.activeCountryIsoCode.toLowerCase()}`;

    // Redirect to new URL
    window.location.href = newURL;
  }

  _updateLanguage() {
    const Weglot = window.Weglot;
    if (!Weglot?.initialized) {
      console.error('Weglot not initialized');
      return;
    }

    try {
      Weglot.switchTo(this.activeLanguage);
      console.log('Language switched to:', this.activeLanguage);
    } catch (error) {
      console.error('Failed to switch language:', error);
    }
  }
}

// Utility function to create option element
const createOption = (text, value, dataset = null, disabled = false, selected = false) => {
  const option = document.createElement('option');
  option.textContent = text;
  
  if (value !== null && value !== undefined) {
    option.value = value;
  }
  
  if (dataset) {
    for (const key in dataset) {
      option.dataset[key] = dataset[key];
    }
  }
  
  if (disabled) {
    option.disabled = true;
  }
  
  if (selected) {
    option.selected = true;
  }
  
  return option;
};

  async function translateArray(array, keys = [], lang = 'fr') {
    const Weglot = window.Weglot;

    if(Weglot?.initialized === true) {
      return new Promise((resolve) => {
        const words = array.flatMap(item =>
          keys.map(key => ({ t: 1, w: item[key] }))
        );

        Weglot.translate({ words, languageTo: lang }, (result) => {
          const translated = array.map((item, i) => {
            const updates = {};
            keys.forEach((key, kIndex) => {
              const index = i * keys.length + kIndex;
              updates[key] = result[index];
            });
            return { ...item, ...updates };
          });
          resolve(translated);
        });
      });
    } else {
      return new Promise((resolve) => {
        resolve(array);
      });
    }
  }

customElements.define("geolocation-markets", GeolocationMarkets);
